import {
  AppliedPaymentCode,
  CartValidationError,
  ProductAgeRestrictionSettings,
  ProductPreOrderSettings,
  StockIndication
} from '@spa/generated/graphql/graphql';
import {
  EnergyEfficiencyDatasheet,
  EnergyEfficiencyLabel,
  EnergyEfficiencyOverview
} from '@spa/components/ProductAssetTypes';
import { CapacityUnitCode } from '../../converters/convertCapacityUnitCode';
import { CurrencyIso } from '../../converters/convertCurrencyIso';
import { CustomOrderType } from './CustomOrderType';
import { DetailedStore } from '@spa/components/storefinder/interfaces';
import { ProductBadge } from '@spa/components/product-info/ProductBadge';
import { QuantityValidationError } from '@spa/lib/cartQuantityValidation';
import { RegisterFailedCause } from '@spa/lib/store/auth/RegisterFailedCause';
import { TitleCode } from '../../converters/convertCustomerTitles';

export interface StateCartPrice {
  readonly currencyIso: CurrencyIso;
  readonly valueWithoutTax: number;
  readonly valueWithTax: number;
}

export interface StateCartAddress {
  readonly titleCode: TitleCode;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly line1: string;
  readonly line2: string;
  readonly additionalAddressInfo: string | null;
  readonly postalCode: string;
  readonly town: string;
  readonly countryIsocode: string;
  readonly email: string | null;
  readonly company: string | null;
}

export interface StateCartDeliveryAddress extends StateCartAddress {
  readonly mobile: string | null;
  readonly storeCode: string | null;
  readonly pickupTime?: string | null;
}

export type StateCartPaymentAddress = StateCartAddress;

export interface StateCartProductImages {
  readonly url: string;
  readonly width: number;
  readonly height: number;
}

export interface StateCartCapacity {
  readonly value: string;
  readonly unitCode: CapacityUnitCode;
}

interface StateCartCategories {
  readonly name: string;
}

export interface StateCartBasePrice {
  readonly value: number;
  readonly capacity: StateCartCapacity;
}

export interface StateCartProduct {
  readonly diaperSize?: string | null;
  readonly diopters?: string | null;
  readonly inkColor?: string | null;
  readonly manufacturerColor?: string | null;
  readonly storageCapacity?: string | null;
  readonly storageUnit?: string | null;
  readonly ageRestrictionSettings: ProductAgeRestrictionSettings | null;
  readonly buyable?: boolean;
  readonly basePrice: StateCartBasePrice | null;
  readonly code: string;
  readonly clothingSize: string | null;
  readonly energyEfficiencyLabel: EnergyEfficiencyLabel | null;
  readonly energyEfficiencyOverview: EnergyEfficiencyOverview | null;
  readonly energyEfficiencyDataSheet: EnergyEfficiencyDatasheet | null;
  readonly giftWithPurchase: boolean | null;
  readonly name: string;
  readonly ownBrand: boolean | null;
  readonly path: string;
  readonly campaignPrice: StateCartPrice | null;
  readonly clickAndCollect?: boolean;
  readonly homeDelivery?: boolean;
  readonly currentPrice: StateCartPrice;
  readonly recommendedRetailPrice: StateCartPrice | null;
  readonly images: ReadonlyArray<StateCartProductImages>;
  readonly hasFreeSamples: boolean | null;
  readonly sizeRange: string | null;
  readonly brand: StateCartCategories | null;
  readonly categories: ReadonlyArray<StateCartCategories>;
  readonly categoryWithParents: ReadonlyArray<StateCartCategories>;
  readonly badges?: ReadonlyArray<ProductBadge> | null;
  readonly stockLevel: number;
  readonly stockLevelIndication: StockIndication;
  readonly capacity: StateCartCapacity;
  readonly preOrderSettings: ProductPreOrderSettings | null;
  readonly releaseDate: string | null;
  readonly visibilityDate: string | null;
  readonly saleMinimumQuantity: number | null;
  readonly saleMaximumQuantity: number | null;
  readonly saleQuantityIncrements: number | null;
  readonly strikePrice: StateCartPrice | null;
  readonly averageRating: number | null;
  readonly reviewCount: number | null;
}

export enum StateCartEntryMode {
  STANDARD = 'STANDARD',
  GIFT_ITEM = 'GIFT_ITEM'
}

export enum StateCartEntryPriceMode {
  PLATFORM = 'Platform',
  EXTERNAL = 'ExternalPrice'
}

export interface StateCartEntryTracking {
  readonly itemListId?: string;
  readonly itemListName?: string;
}

export interface StateCartEntry {
  readonly quantityValidationError?: QuantityValidationError;
  readonly id: string;
  readonly entryMode: StateCartEntryMode;
  readonly hasProductDiscount?: boolean;
  readonly hasSupplyChannel: boolean;
  readonly product: StateCartProduct;
  readonly quantity: number;
  readonly totalPrice: StateCartPrice;
  readonly priceMode: StateCartEntryPriceMode;
  readonly discountOnCartEntryPricePerQuantity: ReadonlyArray<StateCartDiscountOnEntryPricePerQuantity> | null;
  readonly tracking?: StateCartEntryTracking;
}

export enum StateCartPaymentMode {
  AMAZON_PAY = 'AMAZON_PAY',
  KLARNA = 'KLARNA',
  NONE = 'NONE',
  PAY_AT_STORE = 'PAY_AT_STORE',
  PAYPAL = 'PAYPAL'
}

export interface StateCartPayment {
  readonly descriptor: string | null;
  readonly interfaceId: string | null;
  readonly mode: StateCartPaymentMode;
}

export interface StateCartAppliedPaymentGiftCard {
  readonly balance: StateCartDiscountedAmount | null;
  readonly onlineId: string | null;
  readonly remainingBalance: StateCartDiscountedAmount | null;
  readonly transactionId: string | null;
}

export interface StateCartAppliedPayment {
  readonly amountPlanned: StateCartDiscountedAmount | null;
  readonly code: AppliedPaymentCode;
  readonly giftCard: StateCartAppliedPaymentGiftCard | null;
  readonly id: string | null;
  readonly method: string | null;
  readonly paymentInterface: string | null;
}

export interface StateCartDeliveryMode {
  readonly code: string;
  readonly description: string | null;
  readonly freeAbove: StateCartPrice | null;
  readonly price: StateCartPrice;
}

export interface StateCartDiscountCode {
  readonly state?: AddDiscountCodeStatus | null;
  readonly hasBenefit?: boolean | null;
  readonly id: string;
  readonly code: string;
  readonly discounts: ReadonlyArray<string>;
  readonly valid: boolean;
  readonly validUntil: string | null;
  readonly hasFullShippingCostsDiscount: boolean;
  readonly isEmployeeDiscount: boolean;
}

export interface StateCartDiscountedAmount {
  readonly valueWithTax: number;
  readonly currencyIso: CurrencyIso;
}

export enum DiscountType {
  RELATIVE = 'relative',
  ABSOLUTE = 'absolute',
  GIFT = 'giftLineItem'
}

export interface StateCartDiscount {
  readonly id: string;
  readonly name: string;
  readonly value: StateCartDiscountedAmount;
  readonly type: DiscountType;
  readonly absoluteValue?: StateCartDiscountedAmount | null;
  readonly relativeValue?: number | null;
}

export type StateCartDiscountOnTotalPrice = StateCartDiscountOnPrice;

export type StateCartDiscountOnEntryPrice = StateCartDiscountOnPrice;

export interface StateCartDiscountOnEntryPricePerQuantity {
  readonly quantity: number | null;
  readonly discountedPrice: StateCartDiscountOnPrice | null;
}

export interface StateCartDiscountOnPrice {
  readonly totalDiscount: StateCartDiscountedAmount;
  readonly includedDiscounts: ReadonlyArray<StateCartDiscount>;
}

export interface StateCartUser {
  readonly email: string | null;
  readonly id: string;
  readonly type: string;
}

export interface StateCart {
  readonly birthDate: string | null;
  readonly code: string;
  readonly deliveryMode: StateCartDeliveryMode | null;
  readonly deliveryAddress: StateCartDeliveryAddress | null;
  readonly paymentAddress: StateCartPaymentAddress | null;
  readonly entries: ReadonlyArray<StateCartEntry>;
  readonly deliveryCost: StateCartPrice | null;
  readonly subTotal: StateCartPrice | null;
  readonly subTotalWithoutDiscounts: StateCartPrice | null;
  readonly totalPrice: StateCartPrice;
  readonly totalQuantityCount: number;
  readonly payment: StateCartPayment | null;
  readonly payments?: ReadonlyArray<StateCartAppliedPayment>;
  readonly user: StateCartUser;
  readonly discountCodes?: ReadonlyArray<StateCartDiscountCode> | null;
  readonly discountOnTotalPrice?: StateCartDiscountOnTotalPrice | null;
}

export type OptionalStateCarts = Record<CustomOrderType, StateCart | null>;

export interface AddToCurrentCartSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
  readonly productCode: string;
  readonly requestedQuantity: number;
  readonly addedQuantity: number;
  readonly isNewCart: boolean;
  readonly selectedStore?: DetailedStore;
}

interface ProductCodesWithQuantity {
  readonly code: string;
  readonly quantity: number;
}

export interface AddProductsToCurrentCartSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
  readonly isNewCart: boolean;
  readonly allQuantityAdded: boolean;
  readonly productCodesWithQuantities: Array<ProductCodesWithQuantity>;
}

export interface UpdateCurrentCartEntryQuantitySucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
  readonly entryId: string;
  readonly requestedQuantity: number;
  readonly updatedQuantity: number;
}

export enum AddDiscountCodeStatus {
  APPLICATION_STOPPED_BY_PREVIOUS_DISCOUNT = 'ApplicationStoppedByPreviousDiscount',
  APPLIED = 'Applied',
  CONDITIONS_NOT_MET = 'ConditionsNotMet',
  MAX_APPLICATION_REACHED = 'MaxApplicationReached',
  MULTIPLE_CODES_NOT_ALLOWED = 'MultipleCodesNotAllowed',
  NO_BENEFIT = 'NoBenefit',
  NOT_ACTIVE = 'NotActive',
  NOT_VALID = 'NotValid',
  REDEMPTION_ERROR = 'RedemptionError',
  NOT_APPLIED = 'NotApplied'
}

export interface AddDiscountCodeSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string | null;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export type NonAppliedDiscountCodeStatus = Exclude<
  AddDiscountCodeStatus,
  AddDiscountCodeStatus.APPLIED
>;

export interface AddDiscountCodeFailedResult {
  readonly requestedActionId: number;
  readonly failedReason: NonAppliedDiscountCodeStatus;
}

export interface AddDiscountCodeToCurrentCartsResult {
  readonly requestedActionId: number;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export interface AddEmployeeDiscountCodeToCurrentCartsResult {
  readonly requestedActionId: number;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export interface RemoveDiscountCodeResult {
  readonly requestedActionId: number;
  readonly cartId: string | null;
}

export interface StateGiftCardStatus {
  readonly balance: StateCartDiscountedAmount;
  readonly expired: boolean;
  readonly blocked: boolean;
  readonly redeemable: boolean;
}

export interface AddGiftCardToCartSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string | null;
  readonly status: StateGiftCardStatus;
  readonly onlineId: string;
}

export interface AddGiftCardToCartFailedResult extends CurrentCartFailedState {
  readonly errorCode: string | null;
}

export interface RemoveGiftCardFromCartSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string | null;
}

export interface RemoveDiscountCodeFromCartsResult {
  readonly requestedActionId: number;
  readonly codeId: string;
  readonly removedFrom: Array<CustomOrderType>;
  readonly errorsOn: Array<CustomOrderType>;
}

export interface RemoveAllDiscountCodesFromCartsResult {
  readonly requestedActionId: number;
  readonly removedFrom: Array<CustomOrderType>;
  readonly errorsOn: Array<CustomOrderType>;
}

export interface DeleteCurrentCartEntrySucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
  readonly entryId: string;
}

export interface DeleteCurrentCartSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCurrentCartEmailSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCurrentCartContactSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCurrentCartBirthDateSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartDeliveryAddressSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartSelectedStoreSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartDeliveryModeSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartTypeSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartPaymentModeSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface ChangeCartPaymentAddressSucceededResult {
  readonly requestedActionId: number;
  readonly cartId: string;
}

export interface PlaceCartSucceededResult {
  readonly succeeded: true;
  readonly cartId: string;
  readonly orderId: string;
}

export interface RegisterCustomerAndChangeAddressesAndSignInResult {
  readonly requestedActionId: number;
}

export interface RegisterCustomerAndChangeAddressesAndSignInFailedResult {
  readonly requestedActionId: number;
  readonly registerFailedCause: RegisterFailedCause | null;
  readonly signInFailed: boolean;
  readonly cartChangesFailed: boolean;
}

export interface PlaceOrderRedirectAction {
  readonly type: 'redirect';
  readonly url: string;
}

export interface PlaceOrderDisplayKarnaWidgetAction {
  readonly type: 'display-klarna-widget';
  readonly clientToken: string;
}

export type PlaceOrderAction = PlaceOrderRedirectAction | PlaceOrderDisplayKarnaWidgetAction;

export type GiftCardCancellationStatus = 'SUCCEEDED' | 'FAILED';

export enum AddToCurrentCartFailedError {
  UNKNOWN,
  ITEM_MAXIMUM_QUANTITY_VALIDATION_FAILED
}

export interface AddToCurrentCartFailedAction {
  readonly requestedActionId: number;
  readonly failedReason: AddToCurrentCartFailedError;
}

export interface AddProductsToCurrentCartFailedAction {
  readonly requestedActionId: number;
  readonly failedReason: AddToCurrentCartFailedError;
}
export interface PlaceCartFailedResult {
  readonly validationErrors?: ReadonlyArray<CartValidationError> | null;
  readonly succeeded: false;
  readonly action: PlaceOrderAction | null;
  readonly error: string;
  readonly giftCardCancellation: GiftCardCancellationStatus | null;
}

export type PlaceCartResult = PlaceCartSucceededResult | PlaceCartFailedResult;

export interface SucceededStatusEnvelop<A> {
  readonly succeeded: true;
  readonly result: A;
}

export interface FailedStatusEnvelop<A> {
  readonly succeeded: false;
  readonly result: A;
}

export type StatusEnvelop<S, F> = SucceededStatusEnvelop<S> | FailedStatusEnvelop<F>;

export interface PlaceCurrentCartsSucceededResult {
  readonly requestedActionId: number;
  readonly homeDelivery: PlaceCartResult | null;
  readonly collectAtStore: PlaceCartResult | null;
}

export interface PlaceCurrentCartsFailedResult extends CurrentCartFailedState {
  readonly homeDelivery: PlaceCartResult | null;
  readonly collectAtStore: PlaceCartResult | null;
}

export interface CurrentCartFailedState {
  readonly requestedActionId: number;
}

export interface AsyncActionState<S, F extends CurrentCartFailedState = CurrentCartFailedState> {
  readonly pending: ReadonlyArray<number>;
  readonly succeeded: ReadonlyArray<S>;
  readonly failed: ReadonlyArray<F>;
}

export interface CartsState {
  readonly carts: OptionalStateCarts;
  readonly cartsLoaded: boolean;
  readonly lastSyncedServerTimestamp: number | null;
  readonly lastGiftCardOnlineId: string | null;
  readonly giftCardRemovedBecauseCartChanged: boolean;
  readonly pageActions: {
    readonly addToCurrentCart: AsyncActionState<
      AddToCurrentCartSucceededResult,
      AddToCurrentCartFailedAction
    >;
    readonly addProductsToCurrentCart: AsyncActionState<
      AddProductsToCurrentCartSucceededResult,
      AddToCurrentCartFailedAction
    >;
    readonly updateCurrentCartEntryQuantity: AsyncActionState<UpdateCurrentCartEntryQuantitySucceededResult>;
    readonly deleteCurrentCartEntry: AsyncActionState<DeleteCurrentCartEntrySucceededResult>;
    readonly deleteCurrentCart: AsyncActionState<DeleteCurrentCartSucceededResult>;
    readonly addDiscountCode: AsyncActionState<
      AddDiscountCodeSucceededResult,
      AddDiscountCodeFailedResult
    >;
    readonly addDiscountCodeToCurrentCarts: AsyncActionState<
      AddDiscountCodeToCurrentCartsResult,
      AddDiscountCodeToCurrentCartsResult
    >;
    readonly addEmployeeDiscountCodeToCurrentCarts: AsyncActionState<
      AddEmployeeDiscountCodeToCurrentCartsResult,
      AddEmployeeDiscountCodeToCurrentCartsResult
    >;
    readonly removeDiscountCode: AsyncActionState<RemoveDiscountCodeResult>;
    readonly removeDiscountCodeFromCarts: AsyncActionState<
      RemoveDiscountCodeFromCartsResult,
      RemoveDiscountCodeFromCartsResult
    >;
    readonly removeAllDiscountCodesFromCarts: AsyncActionState<
      RemoveAllDiscountCodesFromCartsResult,
      RemoveAllDiscountCodesFromCartsResult
    >;
    readonly addGiftCardToCurrentCart: AsyncActionState<
      AddGiftCardToCartSucceededResult,
      AddGiftCardToCartFailedResult
    >;
    readonly removeGiftCardFromCurrentCart: AsyncActionState<RemoveGiftCardFromCartSucceededResult>;
    readonly changeCurrentCartEmail: AsyncActionState<ChangeCurrentCartEmailSucceededResult>;
    readonly changeCurrentCartContact: AsyncActionState<ChangeCurrentCartContactSucceededResult>;
    readonly changeCurrentCartBirthDate: AsyncActionState<ChangeCurrentCartBirthDateSucceededResult>;
    readonly changeCurrentCartDeliveryAddress: AsyncActionState<ChangeCartDeliveryAddressSucceededResult>;
    readonly changeCurrentCartSelectedStore: AsyncActionState<ChangeCartDeliveryAddressSucceededResult>;
    readonly changeCurrentCartDeliveryMode: AsyncActionState<ChangeCartDeliveryModeSucceededResult>;
    readonly changeCurrentCartType: AsyncActionState<ChangeCartTypeSucceededResult>;
    readonly changeCurrentCartPaymentAddress: AsyncActionState<ChangeCartPaymentAddressSucceededResult>;
    readonly changeCurrentCartPaymentMode: AsyncActionState<ChangeCartPaymentModeSucceededResult>;
    readonly placeCurrentCarts: AsyncActionState<
      PlaceCurrentCartsSucceededResult,
      PlaceCurrentCartsFailedResult
    >;
    readonly registerCustomerAndChangeAddressesAndSignIn: AsyncActionState<
      RegisterCustomerAndChangeAddressesAndSignInResult,
      RegisterCustomerAndChangeAddressesAndSignInFailedResult
    >;
  };
}
